import React from 'react';
import { GradientText } from '../components/ui/GradientText';
import { Users, Rocket, Brain, Target, Zap, Heart, Code2, Globe2, MessageSquare } from 'lucide-react';
import Footer from '../components/Footer';

export default function About() {
  return (
    <div className="bg-black">
      {/* Hero Section */}
      <div className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(79,70,229,0.1),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(219,39,119,0.1),transparent_50%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Our <GradientText>Vision</GradientText>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
             Create exceptional conversational experiences that delight users and transform customer relationships.
            </p>
          </div>
        </div>
      </div>

      {/* Notre Mission */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-white mb-6">
                A user-centered <GradientText>approach</GradientText>
              </h2>
              <p className="text-gray-300 mb-8 text-lg leading-relaxed">
                At Botmaker Agency, we place user experience at the heart of our approach. Our goal is simple: to create chatbots that users love to use. We believe a chatbot should be more than just a tool—it should be a true assistant, intuitive, responsive, and enjoyable to use.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                    <Heart className="w-4 h-4 text-indigo-400" />
                  </div>
                  <span className="text-gray-300">Intuitive design and seamless experience</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <Brain className="w-4 h-4 text-purple-400" />
                  </div>
                  <span className="text-gray-300">Advanced AI for relevant answers</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-pink-500/20 flex items-center justify-center">
                    <Zap className="w-4 h-4 text-pink-400" />
                  </div>
                  <span className="text-gray-300">Optimal performance and responsiveness</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-indigo-500/20 to-pink-500/20 rounded-2xl p-1">
                <div className="bg-black rounded-2xl p-8">
                  <img 
                    src="https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&q=80&w=1000"
                    alt="User Experience"
                    className="rounded-lg shadow-2xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Notre Équipe */}
      <section className="py-20 bg-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">
              A dynamic <GradientText>team</GradientText>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
             Young, passionate and experienced, we combine technical expertise and innovative vision to create the solutions of tomorrow.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
              <Code2 className="w-12 h-12 text-indigo-400 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">Technical Expertise</h3>
              <p className="text-gray-400">A team of developers passionate about AI and modern web technologies.</p>
            </div>

            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
              <Users className="w-12 h-12 text-purple-400 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">Final Customer Focus</h3>
              <p className="text-gray-400">User experience experts dedicated to your visitors’ satisfaction.</p>
            </div>

            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
              <Rocket className="w-12 h-12 text-pink-400 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">Continuous Innovation</h3>
              <p className="text-gray-400">Always on the lookout for the latest advances in AI to improve our solutions.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Notre Engagement */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">
              Our <GradientText>Commitment</GradientText>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              We are committed to providing the best possible experience for every user, staying at the forefront of innovation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-8 border border-white/10">
              <Target className="w-12 h-12 text-indigo-400 mb-6" />
              <h3 className="text-2xl font-semibold text-white mb-4">Operational Excellence</h3>
              <p className="text-gray-300 leading-relaxed">
                We maintain high standards in all our processes, from design to customer support. Our platform is constantly optimized to deliver exceptional performance and rock-solid reliability.
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-8 border border-white/10">
              <Globe2 className="w-12 h-12 text-purple-400 mb-6" />
              <h3 className="text-2xl font-semibold text-white mb-4">Global Impact</h3>
              <p className="text-gray-300 leading-relaxed">
               Our vision extends beyond borders. We are working to make conversational AI accessible to everyone, contributingnt ainsi à transformer la manière dont les entreprises interagissent avec leurs clients à travers le monde.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call-to-Action */}
      <section className="py-20 bg-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-indigo-500/20 via-purple-500/20 to-pink-500/20 rounded-2xl p-1">
            <div className="bg-black rounded-xl p-8 md:p-12 text-center">
              <MessageSquare className="w-16 h-16 text-indigo-400 mx-auto mb-6" />
              <h2 className="text-3xl font-bold text-white mb-4">
                Ready to deliver an exceptional experience to your users?
              </h2>
              <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
                Discover how our AI chatbot can transform your customer relationship and delight your users.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="https://calendly.com/martin-botmaker-agency/demo-botmaker-agency-ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 via-purple-500 to-pink-500 text-white rounded-xl hover:shadow-[0_0_30px_rgba(79,70,229,0.3)] transition-all"
                >
                  Book a démo
                  <Rocket className="w-4 h-4" />
                </a>
                <a 
                  href="/signup"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/5 text-white rounded-xl border border-white/10 hover:bg-white/10 transition-all"
                >
                 Free trial
                  <Zap className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}